import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import * as Yup from 'yup';
import { Box, Button, Card, Flex, Label, Paragraph, Spinner, Text, useColorMode } from 'theme-ui';
import timerIcon from '../../../assets/images/Icon/EstimatedIcon.svg';
import { msToTime } from '../../../../utils/GenerateQueryString';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { Inputform } from '../../../forms-component/InputForm';
import FormSelect from '../../../forms-component/globalSelect';
import { createBCDoneHighlight, getActiveActionByName } from '../../../../store/actions/action.Highlighters';
import toast from 'react-hot-toast';
import { useState, useEffect, useMemo, useRef } from 'react';
import theme from '../../../../theme';
import { useSelector } from 'react-redux';
import { FaCheck } from 'react-icons/fa';
import CustomTooltip from '../../../CustomTooltip';
import { changeEstimationTime, getTaskById, StartTracking } from '../../../../store/actions/project.action';

const BCPCEstimatedTimeModal = ({ estimatedOpen, estimatedClose, status, handleStartTracking, timeEstimate, timeTaken }: any) => {
    const { hours: hoursEstimates, minutes: minutesEstimates, seconds: secondsEstimates } = msToTime(timeEstimate);

    const [displayRemainingMs, setDisplayRemainingMs] = useState<number>(0);
    const [estimateSubmitted, setEstimateSubmitted] = useState<boolean>(false);

    const ALLOWED_OPTIONS = [
        { label: "Plan Check", value: "Plan Check" },
        { label: "Revision", value: "Revision" }
    ];
    const { activeactionnameList, loader } = useSelector((state: any) => state.actionHighlight)
    const [colorMode] = useColorMode();
    const modalHeader = colorMode === 'light' ? "#fff" : "#20283c";
    const titleHeader = colorMode === 'light' ? "#0a0a0a" : "#dadada";
    // Track if reasonType was just changed to show toast after API response
    const [reasonTypeJustChanged, setReasonTypeJustChanged] = useState(false);

    const { tasksView } = useSelector((state: any) => state.project);


    // Build active tasks options from activeactionnameList for all types
    const getActiveTasksOptions = (type: "Revision" | "Plan Check") => {
        if (Array.isArray(activeactionnameList)) {

            return activeactionnameList
                .filter((item: { isCompleted?: boolean; actionType?: string; payload?: { isCompleted?: boolean; taskEstimateList?: Array<{ isTaskDone?: boolean }> } }) => {
                    if (item.actionType !== type) return false;
                    const isCompleted = item?.payload?.isCompleted === true || item?.isCompleted === true;
                    if (isCompleted) {
                        return false;
                    }
                    return true;
                })
                .map((item: any) => ({
                    label: item?.payload?.name || item.name || '',
                    value: item._id
                }));
        }
        return [];
    };
    const [selectedActiveTask, setSelectedActiveTask] = useState<{ label: string; value: string } | null>(null);
    // Track last selected task id per reason type so we can re-select after refresh
    const lastSelectedByReasonRef = useRef<Record<string, string | null>>({});
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [displayEstimateMs, setDisplayEstimateMs] = useState<number>(0);
    // show immediate validation error for minutes input while typing
    const [minValueImmediateError, setMinValueImmediateError] = useState<string | null>(null);

    // Always refresh estimate and remaining time from source when switching tasks
    useEffect(() => {
        if (selectedActiveTask && Array.isArray(activeactionnameList)) {
            const activeTaskObj = activeactionnameList.find(
                (item: any) => item._id === selectedActiveTask.value
            );

            const estimateList = activeTaskObj?.payload?.taskEstimateList;
            let ms = 0;
            let remainingMs = 0;
            if (Array.isArray(estimateList)) {
                const match = estimateList.find((t: any) => t.taskId === tasksView?._id);
                if (match && typeof match.estimatedTime === 'number') {
                    ms = match.estimatedTime;
                }
                if (match && typeof match.spentTime === 'number' && typeof match.estimatedTime === 'number') {
                    remainingMs = Math.max(match.estimatedTime - match.spentTime, 0);
                }
            }
            setDisplayEstimateMs(ms);
            setDisplayRemainingMs(remainingMs);
        } else {
            setDisplayEstimateMs(0);
            setDisplayRemainingMs(0);
        }
    }, [selectedActiveTask?.value, activeactionnameList, tasksView?._id]);
    let hoursEstimate: string | number = 0, minutesEstimate: string | number = 0, secondsEstimate: string | number = 0;
    let remainingTimeText = "00 Hours 00 Minutes 00 seconds";
    // If an active task is selected, get estimatedTime from local state first
    let ms = displayEstimateMs;
    let remainingMs = displayRemainingMs;
    if (selectedActiveTask && Array.isArray(activeactionnameList)) {
        if (!ms || !remainingMs) {
            const activeTaskObj = activeactionnameList.find(
                (item: any) => item._id === selectedActiveTask.value
            );
            const estimateList = activeTaskObj?.payload?.taskEstimateList;

            if (Array.isArray(estimateList)) {
                const match = estimateList.find((t: any) => t.taskId === tasksView?._id);
                if (match && typeof match.estimatedTime === 'number') {
                    ms = match.estimatedTime;
                }
                if (match && typeof match.spentTime === 'number' && typeof match.estimatedTime === 'number') {
                    remainingMs = Math.max(match.estimatedTime - match.spentTime, 0);
                }
            }
        }
        const timeObj = msToTime(ms);
        hoursEstimate = timeObj.hours;
        minutesEstimate = timeObj.minutes;
        secondsEstimate = timeObj.seconds;
        // For remaining time
        const remainingObj = msToTime(remainingMs);
        remainingTimeText = `${remainingObj.hours || "00"} Hours ${remainingObj.minutes || "00"} Minutes ${remainingObj.seconds || "00"} seconds`;
    } else {
        hoursEstimate = 0;
        minutesEstimate = 0;
        secondsEstimate = 0;
    }

    // Only show BC, PC, Rev if highlighter exists in current task
    const highlighterNames = (tasksView?.highlighters?.map((h: any) => {
        return h.name;
    })) || [];
    // Exclude stage name 'IPR' from allowed stage reasons
    const _rawStageName = tasksView?.stageDataId?.temporaryName || tasksView?.stageId?.name || null;
    const stageName = (_rawStageName && typeof _rawStageName === 'string' && _rawStageName.trim().toUpperCase() === 'IPR' && _rawStageName.trim().toLowerCase() === 'ipr-structural') ? null : _rawStageName;

    const filteredEstimatedReason = useMemo(() => {
        const fromHighlighters = ALLOWED_OPTIONS.filter(opt => highlighterNames.includes(opt.label));
        const extras: any[] = [];
        if (stageName && stageName.trim() !== '') {
            if (!fromHighlighters.some(o => o.label === stageName)) {
                extras.push({ label: stageName, value: stageName });
            }
        }
        return [...fromHighlighters, ...extras];
    }, [highlighterNames, stageName]);

    const dispatch = useDispatch();
    const formik = useFormik({
        initialValues: {
            description: "",
            reasonType: (stageName && estimatedOpen) ? stageName : "",
            hoursValue: +hoursEstimate || 0,
            minValue: +minutesEstimate || 0,
        },
        validationSchema: Yup.object({
            description: Yup.string().required('Description is required'),
            reasonType: Yup.string().required('Reason Type is required'),
            hoursValue: Yup.string().matches(
                /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/,
                "not valid"
            ),
            minValue: Yup.number().max(60, "Maximum value is 60")
        }),
        onSubmit: (values) => {

            let spentTimeValue = 0;
            let estimatedTimeValue = displayEstimateMs;

            if (selectedActiveTask && Array.isArray(activeactionnameList)) {
                const activeTaskObj = activeactionnameList.find((item: any) => item._id === selectedActiveTask.value);

                const estimateList = activeTaskObj?.payload?.taskEstimateList;
                if (Array.isArray(estimateList)) {
                    const match = estimateList.find((t: any) => {
                        return String(t?.taskId) === String(tasksView?._id);
                    });

                    if (match) {
                        if (typeof match.spentTime === 'number') {
                            spentTimeValue = match.spentTime;
                        }
                        if (typeof match.estimatedTime === 'number') {
                            estimatedTimeValue = estimatedTimeValue || match.estimatedTime;
                        }
                    }
                }
            }

            if (estimatedTimeValue <= 300000) {
                toast.dismiss();
                toast.error("Estimated time must be greater than 5 minutes to start tracking.", { duration: 4000 });
                return;
            }
            const payload = {
                category: "Task timer",
                projectId: tasksView?.projectId?._id,
                stageId: tasksView?.stageDataId?._id,
                taskId: tasksView?._id,
                actionDetails: {
                    reasonType: values.reasonType,
                    name: selectedActiveTask?.label || null,
                    actionId: selectedActiveTask?.value || null,
                    reason: values.description || null,
                    spentTime: spentTimeValue
                }
            }


            dispatch(StartTracking(payload));
            estimatedClose();
        },
    });

    const { handleSubmit, setFieldValue, values, handleBlur, touched, errors } = formik;

    useEffect(() => {
        if (reasonTypeJustChanged && Array.isArray(activeactionnameList)) {
            if (values?.reasonType === "Plan Check" || values?.reasonType === "Revision") {
                const options = getActiveTasksOptions(values?.reasonType as "Plan Check" | "Revision");
                if (options.length === 0) {
                    toast.dismiss();
                    toast.error("There is no active action", { duration: 4000 });
                }
            }
            setReasonTypeJustChanged(false);
        }
    }, [activeactionnameList, reasonTypeJustChanged, values?.reasonType]);
    const isStageSelected = !!(values?.reasonType && stageName && values.reasonType === stageName);

    const pad = (n: any) => {
        const asNum = Number(n) || 0;
        return String(asNum).padStart(2, '0');
    }


    // user changes or resets immediately after an estimate.
    const prevPopulatedReasonRef = useRef<string | null>(null);
    // immediately after we intentionally reset the inputs.
    useEffect(() => {
        const currentReason = values?.reasonType;
        if (currentReason === prevPopulatedReasonRef.current) return;

        // Stage selected: populate with stage estimate
        if (currentReason && stageName && currentReason === stageName) {
            setFieldValue('hoursValue', Number(hoursEstimates) || 0, true);
            setFieldValue('minValue', Number(minutesEstimates) || 0, true);
            prevPopulatedReasonRef.current = currentReason;
            return;
        }

        // Plan Check or Revision (or other non-stage reason): reset inputs to 0
        if (currentReason && currentReason !== stageName) {
            setFieldValue('hoursValue', 0, true);
            setFieldValue('minValue', 0, true);
            prevPopulatedReasonRef.current = currentReason;
            return;
        }

        prevPopulatedReasonRef.current = null;
    }, [values?.reasonType, stageName, hoursEstimates, minutesEstimates, setFieldValue]);


    useEffect(() => {
        const rt = values?.reasonType;
        if (!rt || rt === stageName) return;
        if (selectedActiveTask && hoursEstimate !== undefined && minutesEstimate !== undefined) {
            setFieldValue('hoursValue', Number(hoursEstimate) || 0, true);
            setFieldValue('minValue', Number(minutesEstimate) || 0, true);
            prevPopulatedReasonRef.current = rt;
        }
    }, [selectedActiveTask?.value, values?.reasonType, hoursEstimate, minutesEstimate]);

    const stageEstimatedDisplay = `${pad(hoursEstimates)}:${pad(minutesEstimates)}:${pad(secondsEstimates)}`;
    const remainingStageMs = Math.max((typeof timeEstimate === 'number' ? timeEstimate : 0) - (typeof timeTaken === 'number' ? timeTaken : 0), 0);
    const remainingStageObj = msToTime(remainingStageMs);
    const stageRemainingWords = `${pad(remainingStageObj.hours)} Hours ${pad(remainingStageObj.minutes)} Minutes ${pad(remainingStageObj.seconds)} seconds`;
    const formHours = Number(values.hoursValue) || 0;
    const formMinutes = Number(values.minValue) || 0;
    // compare form inputs directly to the displayed estimates (stage vs active task)
    const matchesStage = formHours === Number(hoursEstimates) && formMinutes === Number(minutesEstimates);
    const matchesActive = formHours === Number(hoursEstimate) && formMinutes === Number(minutesEstimate);
    const isEstimateEnabled = (formHours > 0 || formMinutes > 0) && !(isStageSelected ? matchesStage : matchesActive);
    const handleEstimateInputChange = (field: string, value: any) => {
        setFieldValue(field, value, true);
    };

    useEffect(() => {
        if (!estimatedOpen) return;
        // derive authoritative hours/minutes from either the selected active task or the stage
        const h = selectedActiveTask ? (Number(hoursEstimate) || 0) : (Number(hoursEstimates) || 0);
        const m = selectedActiveTask ? (Number(minutesEstimate) || 0) : (Number(minutesEstimates) || 0);
        // If current form values differ, set them to the stored estimate so comparison works
        if ((Number(values.hoursValue) || 0) !== h || (Number(values.minValue) || 0) !== m) {
            setFieldValue('hoursValue', h, true);
            setFieldValue('minValue', m, true);
        }
    }, [estimatedOpen, selectedActiveTask?.value, hoursEstimate, minutesEstimate, hoursEstimates, minutesEstimates, setFieldValue]);



    // Consider only the actual stage name as a stage reason
    // (Plan Check / Revision should NOT be treated as a stage reason)
    const isStageReason = (reason?: string | null) => {
        return !!(reason && typeof reason === 'string' && stageName && reason === stageName);
    }

    // Always show active tasks dropdown if reasonType is selected and there are options available
    const activeTasksOptions = getActiveTasksOptions(values?.reasonType as "Plan Check" | "Revision");
    const showActiveTasksDropdown = values?.reasonType && activeTasksOptions.length > 0;
    const handleReasonTypeChange = (selectedOption: any) => {
        const selectedReason = selectedOption?.value;
        setFieldValue("reasonType", selectedReason, true);

        // Get new options for active tasks
        const newActiveTasksOptions = getActiveTasksOptions(selectedReason as "Plan Check" | "Revision");
        // If selectedActiveTask is not in new options, clear selection
        if (
            selectedActiveTask &&
            !newActiveTasksOptions.some(opt => opt.value === selectedActiveTask.value)
        ) {
            setSelectedActiveTask(null);
        }


        if (selectedReason && (selectedReason === "Plan Check" || selectedReason === "Revision")) {
            const actionObject = {
                projectId: tasksView?.projectId?._id,
                stageId: tasksView?.stageDataId?._id,
                taskId: tasksView?._id,
                isActive: true
            };
            setReasonTypeJustChanged(true);
            dispatch(getActiveActionByName(actionObject, { name: selectedReason }));
        }
    };
    const handleActiveTask = () => {
        setShowConfirmation(true);
    }
    const handleConfirmDelivery = () => {
        setShowConfirmation(false);
        const payload = {
            actionType: values.reasonType,
            stageId: tasksView?.stageDataId?._id,
            isTimer: true,
            taskEstimateList: [
                {
                    taskId: tasksView?._id,
                    isTaskDone: true
                }
            ]
        };

        dispatch(createBCDoneHighlight(payload, selectedActiveTask?.value, () => {
            estimatedClose();
        }));
    };
    const timeSpent = msToTime(timeTaken);
    const spentTime = `${timeSpent?.hours}:${timeSpent?.minutes}:${timeSpent?.seconds?.toString().padStart(2, '0')}`

    const handleTimeEstimate = () => {
        let oldTimeEstimate = 0;
        let spentTimeValue = 0;
        if (selectedActiveTask && Array.isArray(activeactionnameList)) {
            const activeTaskObj = activeactionnameList.find(
                (item: any) => item._id === selectedActiveTask.value
            );
            const estimateList = activeTaskObj?.payload?.taskEstimateList;

            if (Array.isArray(estimateList)) {
                const match = estimateList.find((t: any) => t.taskId === tasksView?._id);
                if (match && typeof match.estimatedTime === 'number') {
                    oldTimeEstimate = match.estimatedTime;
                }
                if (match && typeof match.spentTime === 'number') {
                    spentTimeValue = match.spentTime;
                }
            }
        }

        const inputTimeMs = (+values.hoursValue * 60 * 60 * 1000) + (+values.minValue * 60 * 1000);

        if (isStageSelected) {
            const newTime = inputTimeMs;
            if (newTime <= timeTaken) {
                toast.error(`New estimated time must be greater than the time already spent.Your spent time is ${spentTime}`);
                return;
            }
            const payload = {
                newTime,
                description: values.description,
                reasonType: values.reasonType,
            };

            dispatch(changeEstimationTime(payload, tasksView?._id));
            setEstimateSubmitted(true);

            // estimatedClose();
            return;
        }


        let oldNewTime = 0;
        if (selectedActiveTask && Array.isArray(activeactionnameList)) {
            const activeTaskObj = activeactionnameList.find(
                (item: any) => item._id === selectedActiveTask.value
            );
            const estimateList = activeTaskObj?.payload?.taskEstimateList;
            if (Array.isArray(estimateList)) {
                const match = estimateList.find((t: any) => t.taskId === tasksView?._id);
                if (match && typeof match.estimatedTime === 'number') {
                    oldNewTime = match.estimatedTime;
                }
            }
        }
        const newTime = timeEstimate - oldNewTime + inputTimeMs;

        const payload = {
            actionType: values.reasonType,
            stageId: tasksView?.stageDataId?._id,
            taskEstimateList: [{
                taskId: tasksView?._id,
                newTime: Math.max(0, newTime),
                estimatedTime: inputTimeMs
            }]
        };

        // remember the currently selected task id per reason so we can re-select it after refresh
        if (selectedActiveTask && selectedActiveTask.value) {
            const reason = values?.reasonType;
            if (reason) {
                lastSelectedByReasonRef.current[reason] = selectedActiveTask?.value;
            }
        }
        setEstimateSubmitted(true);
        dispatch(createBCDoneHighlight(payload, selectedActiveTask?.value, () => {
            toast.success("Time Estimated successfully");
            const newEstimatedTotal = inputTimeMs;
            setDisplayEstimateMs(newEstimatedTotal);
            const newRemaining = Math.max(0, newEstimatedTotal - (spentTimeValue || 0));
            setDisplayRemainingMs(newRemaining);
            setFieldValue('hoursValue', 0, true);
            setFieldValue('minValue', 0, true);
            setEstimateSubmitted(true);
            // Immediately refresh the active actions list for the current reason type
            if (values.reasonType && (values.reasonType === "Plan Check" || values.reasonType === "Revision")) {
                const actionObject = {
                    projectId: tasksView?.projectId?._id,
                    stageId: tasksView?.stageDataId?._id,
                    taskId: tasksView?._id,
                    isActive: true
                };
                dispatch(getActiveActionByName(actionObject, { name: values.reasonType }));
                // optimistic re-select: keep the same selected task in the dropdown immediately
                if (selectedActiveTask && selectedActiveTask.value) {
                    setSelectedActiveTask({ label: selectedActiveTask.label || '', value: selectedActiveTask.value });
                }
                dispatch(getTaskById(tasksView?._id));
            }
        }));
    }

    useEffect(() => {
        setEstimateSubmitted(false);
    }, [selectedActiveTask, values?.hoursValue, values?.minValue]);
    const closeButton = (
        <button
            type="button"
            aria-label="Close"
            onClick={estimatedClose}
            style={{
                color: colorMode === 'dark' ? '#fff' : '#000',
                background: 'transparent',
                border: 'none',
                fontSize: 23,
                lineHeight: 1,
                padding: 0,
                cursor: 'pointer'
            }}
        >
            <span aria-hidden="true">&times;</span>
        </button>
    );
    return (
        <Modal isOpen={estimatedOpen} centered size='lg' toggle={estimatedClose} >
            <ModalHeader toggle={estimatedClose} close={closeButton} style={{ backgroundColor: modalHeader }}>
                <Paragraph>
                    <img src={timerIcon} alt='' className='mx-2' style={{ filter: colorMode === 'dark' ? 'invert(1) grayscale(100%) brightness(200%)' : 'none' }} />
                    <Text sx={{ color: titleHeader }}>Manage BC/PC/REV Estimated time</Text>
                </Paragraph>
            </ModalHeader>
            <form onSubmit={handleSubmit}>
                <ModalBody className='scrollable-modal-body text-center' style={{ backgroundColor: modalHeader }}>
                    <Card sx={{ boxShadow: "none", border: `${theme?.colors?.lightbackground}`, borderRadius: "12px", padding: "12px 20px", backgroundColor: modalHeader }}>
                        <Text sx={{ color: `${theme.colors?.muted}` }}>Estimated Time</Text>
                        <Paragraph sx={{ fontSize: "32px", fontWeight: "400", mt: "1.5rem", color: titleHeader }}>
                            {isStageSelected ? stageEstimatedDisplay : `${hoursEstimate || "00"}:${minutesEstimate || "00"}:${secondsEstimate || "00"}`}
                        </Paragraph>
                        <Text sx={{ color: `${theme.colors?.muted}`, fontSize: "10px" }}>
                            Remaining Time: {isStageSelected ? stageRemainingWords : remainingTimeText}
                        </Text>
                        {selectedActiveTask && tasksView?.taskEstimateList && Array.isArray(tasksView.taskEstimateList) && (() => {
                            if (displayRemainingMs) {
                                const remainingObj = msToTime(displayRemainingMs);
                                return (
                                    <Text sx={{ color: `${theme.colors?.primary}`, fontSize: "12px", mt: "0.5rem" }}>
                                        Remaining Time: {`${remainingObj.hours || "00"}:${remainingObj.minutes || "00"}:${remainingObj.seconds || "00"}`} ({remainingObj.hours || "00"} Hours {remainingObj.minutes || "00"} Minutes {remainingObj.seconds || "00"} seconds)
                                    </Text>
                                );
                            } else {
                                const match = tasksView.taskEstimateList.find((t: any) => t.taskId === tasksView?._id);
                                if (match && typeof match.estimatedTime === 'number' && typeof match.spentTime === 'number') {
                                    const remainingMs = Math.max(match.estimatedTime - match.spentTime, 0);
                                    const remainingObj = msToTime(remainingMs);
                                    return (
                                        <Text sx={{ color: `${theme.colors?.primary}`, fontSize: "12px", mt: "0.5rem" }}>
                                            Remaining Time: {`${remainingObj.hours || "00"}:${remainingObj.minutes || "00"}:${remainingObj.seconds || "00"}`} ({remainingObj.hours || "00"} Hours {remainingObj.minutes || "00"} Minutes {remainingObj.seconds || "00"} seconds)
                                        </Text>
                                    );
                                }
                                return null;
                            }
                        })()}
                        <Flex sx={{ gap: "1rem", alignItems: "center", justifyContent: "center", mt: "1.5rem" }}>
                            <Button
                                type='button'
                                variant='startButton'
                                disabled={((!selectedActiveTask) && !isStageSelected) || (values.minValue === 0 && values.hoursValue === 0)}
                                sx={{ borderColor: titleHeader, background: (((!selectedActiveTask) && !isStageSelected && colorMode === 'light')) ? `#ccc` : (colorMode === 'light' ? `white` : `#20283c`), color: titleHeader, padding: "8px 12px 8px 12px" }}
                                onClick={() => {
                                    if (!selectedActiveTask && !isStageSelected) return;
                                    let newMin = 0;
                                    if (Number(values.minValue) === 30) {
                                        newMin = 0;
                                    } else if (Number(values.minValue) === 0) {
                                        if (Number(values.hoursValue) > 0) {
                                            setFieldValue("hoursValue", Number(values.hoursValue) - 1);
                                        }
                                        newMin = Number(values.minValue) + 30;
                                    } else {
                                        newMin = Number(values.minValue) - 30;
                                    }
                                    newMin = Math.max(0, Math.min(60, Number(newMin)));
                                    setFieldValue("minValue", newMin);
                                }}>
                                - 30 min <Text sx={{ color: `${theme?.colors?.primary}` }}>Subtract</Text>
                            </Button>
                            <Inputform
                                label={""}
                                Placeholder={`${hoursEstimate}`}
                                Name={"hoursValue"}
                                touched={touched?.hoursValue}
                                errors={errors?.hoursValue}
                                values={values.hoursValue}
                                onBlur={handleBlur}
                                type='number'
                                sx={{ width: "65px", color: titleHeader }}
                                OnChange={(event: any) => {
                                    if (!selectedActiveTask && !isStageSelected) return;
                                    const hoursValue = event.target.value;
                                    handleEstimateInputChange("hoursValue", hoursValue);
                                }}
                                disabled={(!selectedActiveTask && !isStageSelected)}
                            />:
                            <Inputform
                                label={""}
                                Placeholder={`${minutesEstimate}`}
                                Name={"minValue"}
                                type='number'
                                values={values.minValue}
                                onBlur={handleBlur}
                                sx={{ width: "65px", color: titleHeader }}
                                OnChange={(event: any) => {
                                    if (!selectedActiveTask && !isStageSelected) return;
                                    const minValueRaw = event.target.value;
                                    // allow empty input
                                    if (minValueRaw === '' || minValueRaw === null) {
                                        setMinValueImmediateError(null);
                                        handleEstimateInputChange("minValue", minValueRaw);
                                        return;
                                    }
                                    const minValueNum = Number(minValueRaw);
                                    if (Number.isNaN(minValueNum)) {
                                        setMinValueImmediateError(null);
                                        handleEstimateInputChange("minValue", minValueRaw);
                                        return;
                                    }
                                    if (minValueNum > 60) {
                                        // do not accept the change; show immediate error and keep previous valid value
                                        setMinValueImmediateError('Maximum value is 60');
                                        return;
                                    }
                                    setMinValueImmediateError(null);
                                    handleEstimateInputChange("minValue", minValueNum);
                                }}
                                disabled={(!selectedActiveTask && !isStageSelected)}
                            />
                            <Button
                                type='button'
                                variant='startButton'
                                disabled={((!selectedActiveTask) && !isStageSelected)}
                                sx={{ borderColor: titleHeader, background: (((!selectedActiveTask) && !isStageSelected && colorMode === 'light')) ? `#ccc` : (colorMode === 'light' ? `white` : `#20283c`), color: titleHeader, padding: "8px 12px 8px 12px" }}
                                onClick={() => {
                                    if (!selectedActiveTask && !isStageSelected) return;
                                    // add 30 minutes but clamp to max 60
                                    if (Number(values.minValue) === 30) {
                                        setFieldValue("hoursValue", Number(values.hoursValue) + 1);
                                        setFieldValue("minValue", 0);
                                    } else {
                                        const computed = Number(values.minValue) + 30;
                                        const newMin = Math.max(0, Math.min(60, computed));
                                        setFieldValue("minValue", newMin);
                                    }
                                }}>
                                + 30 min <Text sx={{ color: `${theme?.colors?.primary}` }}>Extend</Text>
                            </Button>
                        </Flex>
                        <Box>
                            {minValueImmediateError ? (
                                <Text variant="Inter15Regular30lh" className="text-danger" sx={{ my: "5px", minHeight: "12px", lineHeight: '10px' }}>
                                    {minValueImmediateError}
                                </Text>
                            ) : ((errors.minValue && touched.minValue) && (
                                <Text variant="Inter15Regular30lh" className="text-danger" sx={{ my: "5px", minHeight: "12px", lineHeight: '10px' }}>
                                    {errors.minValue.toString()}
                                </Text>
                            ))}
                        </Box>
                        <Box sx={{ mt: "1rem", textAlign: "start" }}>
                            <Label sx={{ fontSize: '14px', fontWeight: 500, color: titleHeader }} className={`form-label mt-1 ${errors.reasonType && touched.reasonType ? 'is-invalid' : ''}`}>
                                Reason Type<span className="mx-1 text-danger">*</span>
                            </Label>
                            <FormSelect
                                value={values?.reasonType}
                                placeholder={"Select Reason Type"}
                                options={filteredEstimatedReason || []}
                                isMulti={false}
                                touched={touched?.reasonType}
                                errors={errors?.reasonType}
                                onBlur={handleBlur}
                                Name='reasonType'
                                onChange={handleReasonTypeChange}
                            />
                        </Box>
                        {showActiveTasksDropdown && (
                            <Box sx={{ mt: "1rem", textAlign: "start" }}>
                                <Label sx={{ fontSize: '14px', fontWeight: 500, color: titleHeader }} className="form-label mt-1">Active Tasks</Label>
                                <Flex sx={{ gap: "0.5rem", alignItems: "end" }}>
                                    <Box sx={{ flex: 1 }}>
                                        <FormSelect
                                            value={selectedActiveTask?.value}
                                            placeholder={`Select Active ${values?.reasonType} Task`}
                                            options={activeTasksOptions}
                                            isMulti={false}
                                            onChange={(option: any) => {
                                                // set selection and remember it per reason
                                                setSelectedActiveTask(option);
                                                const reason = values?.reasonType;
                                                if (reason && option && option.value) {
                                                    lastSelectedByReasonRef.current[reason] = option.value;
                                                }
                                            }}
                                        />
                                    </Box>
                                    {selectedActiveTask && (
                                        <CustomTooltip id={`active-task-check-${selectedActiveTask?.value || 'none'}`} title="Task Complete">
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '36px',
                                                    height: '36px',
                                                    backgroundColor: '#dcdcdc',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    marginBottom: '5px',
                                                    color: '#6c757d',
                                                    transition: 'background-color 150ms ease, color 150ms ease',
                                                    '&:hover': {
                                                        backgroundColor: '#28a745',
                                                        color: '#ffffff'
                                                    }
                                                }}
                                                onClick={handleActiveTask}
                                                role="button"
                                                aria-label="Mark active task complete"
                                            >
                                                <FaCheck size={14} />
                                            </Box>
                                        </CustomTooltip>
                                    )}
                                    <Modal isOpen={showConfirmation} toggle={() => setShowConfirmation(false)} size="sm" centered>
                                        <ModalHeader toggle={() => setShowConfirmation(false)} style={{ backgroundColor: modalHeader, color: titleHeader }}>Confirm Completion</ModalHeader>
                                        <ModalBody style={{ backgroundColor: modalHeader, color: titleHeader }}>
                                            <Text>Are you sure you want to completed this task?</Text>
                                        </ModalBody>
                                        <ModalFooter style={{ backgroundColor: modalHeader }}>
                                            <Button variant="CancelButton" sx={{ mr: 2 }} onClick={() => setShowConfirmation(false)}>No</Button>
                                            <Button variant="SaveButton" onClick={handleConfirmDelivery}>Yes</Button>
                                        </ModalFooter>
                                    </Modal>
                                </Flex>
                            </Box>
                        )}
                        <Box sx={{ mt: "1rem", textAlign: "start" }}>
                            <Inputform
                                label={"Description"}
                                Placeholder={`Description...`}
                                Name={"description"}
                                sx={{ color: titleHeader }}
                                touched={touched?.description}
                                errors={errors?.description}
                                asterisk={true}
                                values={values.description}
                                onBlur={handleBlur}
                                OnChange={(event: any) => {
                                    if (!selectedActiveTask && !isStageSelected) return;
                                    const description = event.target.value;
                                    setFieldValue("description", description, true);
                                }}
                                disabled={(!selectedActiveTask && !isStageSelected)}
                            />
                        </Box>
                    </Card>
                </ModalBody>
                <ModalFooter className='gap-2 justify-content-end p-3' style={{ backgroundColor: modalHeader }}>
                    <Button
                        variant='SaveButton'
                        type='button'
                        sx={{
                            ml: 2,
                            backgroundColor: theme.colors?.primary,
                            color: 'white',
                            padding: '8px 12px',
                            opacity: (!isEstimateEnabled || estimateSubmitted) ? 0.5 : 1,
                            cursor: (!isEstimateEnabled || estimateSubmitted) ? 'not-allowed' : 'pointer'
                        }}
                        onClick={handleTimeEstimate}
                        disabled={!isEstimateEnabled || loader || estimateSubmitted}
                    >
                        Estimate Time
                    </Button>
                    <Button
                        variant='SaveButton'
                        type='button'
                        onClick={() => {
                            const reason = values?.reasonType;
                            if (isStageReason(reason)) {

                                handleStartTracking({ isStartedNormal: true });
                                return;
                            }
                            handleSubmit();
                        }}
                        sx={{
                            ml: 2,
                            opacity: 1,
                            padding: '8px 12px',
                            cursor: 'pointer'
                        }}
                        disabled={loader}
                    >
                        Start Timer
                    </Button>
                    {loader && (
                        <Box sx={{ position: "absolute", inset: "0", width: "100%", height: "100%", backgroundColor: 'rgba(255, 255, 255, 0.5)', display: "grid", placeContent: "center" }}>
                            <Spinner />
                        </Box>
                    )}
                </ModalFooter>
            </form>
        </Modal>
    );
};

export default BCPCEstimatedTimeModal